import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import FileUploadModal from './FileUploadModal';
import { Paperclip, Calculator, Camera, ToggleOn, ToggleOff, VolumeUp } from 'react-bootstrap-icons';
import { useDropzone } from 'react-dropzone';
import { uploadAndProcessFile } from "../utils/fileUpload";
import { useAuth } from "./auth/AuthProvider";
import { useBedrockClient } from '../context/BedrockClientContext';
import { useDynamoUtils } from '../context/DynamoUtilsContext';
import { useSession } from '../context/SessionContext';

// Character limit for text inputs to prevent abuse
const CHARACTER_LIMIT = 2000;

// Helper to detect mobile/tablet devices
const isMobileOrTablet = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const ChatInput = forwardRef(({ 
  isStreaming, 
  onSend, 
  onFileUpload, 
  disabled, 
  placeholder, 
  handleStopGeneration, 
  setInitialMessage, 
  externalBusy = false, 
  rows = 1,
  showSuggestions,
  onToggleSuggestions,
  enableTts,
  onToggleTts,
  sessionId,
  ttsLimitReached = false,
  ttsLimitInfo = null,
  chatLimitReached = false,
  chatLimitInfo = null,
}, ref) => {
  const [message, setMessage] = useState('');
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [fileUploadMode, setFileUploadMode] = useState('upload');
  const [showMathSymbols, setShowMathSymbols] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const textareaRef = useRef(null);
  const { user } = useAuth(); // Get user from auth context
  const bedrockClientRef = useBedrockClient();
  const dynamoUtilsRef = useDynamoUtils();
  const { sessionId: globalSessionId } = useSession();
  const isMobile = isMobileOrTablet();
  
  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    setValue: (value) => {
      setMessage(value);
      // Timeout ensures state is updated before focusing
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    },
    focus: () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    },
    clear: () => setMessage('')
  }));
  
  // Effect to handle incoming initial message from parent
  useEffect(() => {
    if (setInitialMessage && typeof setInitialMessage === 'string' && setInitialMessage.trim()) {
      setMessage(setInitialMessage);
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [setInitialMessage]);

  // Define common math symbols
  const mathSymbols = [
    { symbol: '∑', description: 'Summation' },
    { symbol: '∫', description: 'Integral' },
    { symbol: '∏', description: 'Product' },
    { symbol: '√', description: 'Square Root' },
    { symbol: 'π', description: 'Pi' },
    { symbol: '±', description: 'Plus-Minus' },
    { symbol: '∞', description: 'Infinity' },
    { symbol: '≈', description: 'Approximately Equal' },
    { symbol: '≠', description: 'Not Equal' },
    { symbol: '≤', description: 'Less Than or Equal' },
    { symbol: '≥', description: 'Greater Than or Equal' },
    { symbol: '÷', description: 'Division' },
    { symbol: '×', description: 'Multiplication' },
    { symbol: '∂', description: 'Partial Derivative' },
    { symbol: 'θ', description: 'Theta' },
    { symbol: 'α', description: 'Alpha' },
    { symbol: 'β', description: 'Beta' },
    { symbol: 'Δ', description: 'Delta' },
    { symbol: '∈', description: 'Element Of' },
    { symbol: '∉', description: 'Not Element Of' },
  ];
  
  // Auto resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  // Close math symbols popup when clicking outside
  useEffect(() => {
    if (showMathSymbols) {
      const closeSymbols = (e) => {
        if (!e.target.closest('.math-symbols-popup') && 
            !e.target.closest('.math-symbols-btn')) {
          setShowMathSymbols(false);
        }
      };
      
      document.addEventListener('click', closeSymbols);
      return () => document.removeEventListener('click', closeSymbols);
    }
  }, [showMathSymbols]);

  const handleSubmit = () => {
    if (!message.trim() || disabled) return;
    onSend(message);
    setMessage('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Process file upload
  const processFile = async (file) => {
    try {
      setUploadBusy(true);
      setErrors([]);
      
      // Check file size
      const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
      if (file.size > MAX_BYTES) {
        setErrors([`${file.name}: larger than 5 MB`]);
        setUploadBusy(false);
        return false;
      }
      
      // Pass the user ID to the upload function
      const userId = user?.sub;
      if (!userId) {
        setErrors(["Please log in to upload files"]);
        setUploadBusy(false);
        return false;
      }
      
      const effectiveSessionId = sessionId || globalSessionId;

      const result = await uploadAndProcessFile(file, {
        userId,
        sessionId: effectiveSessionId,
        bedrockClient: bedrockClientRef.current,
        dynamoUtils: dynamoUtilsRef.current,
      });
      
      if (result) {
        if (onFileUpload) {
          onFileUpload(result);
        }
        setUploadBusy(false);
        return true;
      } else {
        setErrors([`${file.name}: processing failed`]);
        setUploadBusy(false);
        return false;
      }
    } catch (e) {
      setErrors([`${file.name}: upload error: ${e.message}`]);
      console.error("File upload error:", e);
      setUploadBusy(false);
      return false;
    }
  };
  
  // Configure dropzone for direct file drops on chat input
  const { getRootProps, getInputProps, open } = useDropzone({
    // Accepted image MIME types/extensions for drag-and-drop uploads.
    // Extensions must include a leading dot or react-dropzone will log a
    // warning and ignore the entry.
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".webp", ".heic"]
    },
    noClick: true, // Don't open file dialog on click
    noKeyboard: true, // Don't open file dialog on keypress
    maxSize: 5 * 1024 * 1024, // 5 MB
    multiple: false,
    disabled: disabled || isStreaming || uploadBusy,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        processFile(acceptedFiles[0]);
      }
    },
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false)
  });
  
  const handleFileUploadClick = () => {
    setFileUploadMode('upload');
    setShowFileUploadModal(true);
  };

  const handleScreenshotClick = () => {
    // On mobile, we directly open the camera
    if (isMobile) {
      captureFromCamera();
    } else {
      // On desktop, we show the modal with screenshot instructions
      setFileUploadMode('upload');
      setShowFileUploadModal(true);
    }
  };
  
  // Function to capture from camera (mobile)
  const captureFromCamera = () => {
    // Only proceed on mobile/tablet devices
    if (!isMobile) return;
    
    // Create a file input element programmatically
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'camera'; // This will open the camera on mobile devices
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Process the captured image
        processFile(file);
      }
    };
    
    // Trigger the file input click
    input.click();
  };
  
  const handleFileUploadSuccess = (fileData) => {
    setShowFileUploadModal(false);
    if (onFileUpload) {
      onFileUpload(fileData);
    }
  };

  // Insert math symbol at cursor position
  const insertSymbol = (symbol) => {
    if (textareaRef.current) {
      const cursorPos = textareaRef.current.selectionStart;
      const before = message.slice(0, cursorPos);
      const after = message.slice(cursorPos);
      const updated = before + symbol + after;
      setMessage(updated);
      
      // Re-focus and move cursor after the inserted symbol
      setTimeout(() => {
        textareaRef.current.focus();
        const newPos = cursorPos + symbol.length;
        textareaRef.current.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      setMessage(prev => prev + symbol);
    }
    
    // Close the symbols popup after selection
    setShowMathSymbols(false);
  };

  // Toggle math symbols popup
  const toggleMathSymbols = () => {
    setShowMathSymbols(prev => !prev);
  };

  return (
    <div 
      className={`chat-input-container ${isDragActive ? 'drag-active' : ''} ${uploadBusy ? 'busy' : ''}`}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      
      {(uploadBusy || externalBusy) && (
        <div className="upload-overlay">
          <div className="spinner"></div>
          <p>Processing file...</p>
        </div>
      )}
      
      <textarea 
        ref={textareaRef}
        className="chat-input"
        value={message}
        onChange={(e) => {
          const value = e.target.value;
          // Enforce character limit
          if (value.length <= CHARACTER_LIMIT) {
            setMessage(value);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || (isDragActive ? "Drop your image here..." : "Type your question or message here...")}
        disabled={isStreaming || disabled || uploadBusy || chatLimitReached}
      ></textarea>
      
      {errors.length > 0 && (
        <div className="chat-input-errors">
          {errors.map((err, i) => (
            <p key={i} className="error-message">{err}</p>
          ))}
        </div>
      )}

      {chatLimitReached && (
        <div className="chat-limit-warning">
          Chat disabled - {chatLimitInfo?.limitType || 'usage'} limit reached
        </div>
      )}
      
      <div className="input-actions">
        <div className="action-buttons" style={{ display: 'flex', gap: '10px' }}>
          {/* Suggestions toggle button */}
          {onToggleSuggestions && (
            <button
              className="suggestions-toggle"
              onClick={onToggleSuggestions}
              title={showSuggestions ? "Hide suggestions" : "Show suggestions"}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                padding: '8px 10px',
                background: showSuggestions ? '#f0f8f0' : 'none',
                border: showSuggestions ? '1px solid #4a9f5a' : '1px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                outline: 'none',
                color: showSuggestions ? '#4a9f5a' : '#666',
                fontSize: '0.85rem',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              {showSuggestions ? 
                <ToggleOn size={20} style={{ color: '#4a9f5a' }} /> : 
                <ToggleOff size={20} style={{ color: '#666' }} />}
              <span>Suggestions</span>
            </button>
          )}

          {/* TTS toggle button commented out - remove these comments to restore TTS toggle UI
          {onToggleTts && (
            <button
              className={`tts-toggle ${ttsLimitReached ? 'limit-reached' : ''}`}
              onClick={ttsLimitReached ? undefined : onToggleTts}
              title={
                ttsLimitReached 
                  ? `Voice feature disabled - ${ttsLimitInfo?.limitType || 'usage'} limit reached`
                  : (enableTts ? "Turn off text-to-speech" : "Turn on text-to-speech")
              }
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                padding: '8px 10px',
                background: ttsLimitReached ? '#ffebee' : (enableTts ? '#f0f8f0' : 'none'),
                border: ttsLimitReached ? '1px solid #f44336' : (enableTts ? '1px solid #4a9f5a' : '1px solid transparent'),
                borderRadius: '8px',
                cursor: ttsLimitReached ? 'not-allowed' : 'pointer',
                outline: 'none',
                color: ttsLimitReached ? '#f44336' : (enableTts ? '#4a9f5a' : '#666'),
                fontSize: '0.85rem',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                opacity: ttsLimitReached ? 0.6 : 1
              }}
            >
              {ttsLimitReached ? (
                <>
                  <ToggleOff size={20} style={{ color: '#f44336' }} />
                  <VolumeUp size={18} style={{ color: '#f44336' }} />
                  <span>Voice (limit reached)</span>
                </>
              ) : (
                <>
                  {enableTts ? 
                    <ToggleOn size={20} style={{ color: '#4a9f5a' }} /> : 
                    <ToggleOff size={20} style={{ color: '#666' }} />}
                  <VolumeUp size={18} style={{ color: enableTts ? '#4a9f5a' : '#666' }} />
                  <span>Voice</span>
                </>
              )}
            </button>
          )}
          */}

          <div className="math-symbols-wrapper" style={{ position: 'relative' }}>
            <button
              className="math-symbols-btn"
              onClick={toggleMathSymbols}
              title="Math Symbols"
              disabled={isStreaming || disabled || uploadBusy}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <Calculator size={22} style={{ color: '#333' }} />
            </button>
            
            {showMathSymbols && (
              <div className="math-symbols-popup" style={{
                position: 'absolute',
                bottom: '40px',
                right: '0',
                width: '300px',
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                zIndex: 1000,
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {mathSymbols.map((item, index) => (
                  <button
                    key={index}
                    className="math-symbol-item"
                    onClick={() => insertSymbol(item.symbol)}
                    title={item.description}
                    style={{
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      background: '#f9f9f9',
                      cursor: 'pointer',
                      fontSize: '18px'
                    }}
                  >
                    {item.symbol}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Only show camera button on mobile/tablet devices */}
          {isMobile && (
            <button
              className="screenshot-btn"
              onClick={handleScreenshotClick}
              title="Take a photo with camera"
              disabled={isStreaming || disabled || uploadBusy}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <Camera size={22} style={{ color: '#333' }} />
            </button>
          )}

          <button
            className="upload-btn"
            onClick={handleFileUploadClick}
            title="Upload file"
            disabled={isStreaming || disabled || uploadBusy}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <Paperclip size={22} style={{ color: '#333', background: 'none' }} />
          </button>
        </div>
        
        {isStreaming ? (
          <button 
            className="stop-btn"
            onClick={handleStopGeneration}
            title="Stop generating"
          >
            Stop
          </button>
        ) : (
          <button 
            className="send-btn"
            onClick={handleSubmit}
            disabled={!message.trim() || disabled || uploadBusy || chatLimitReached}
          >
           Send
          </button>
        )}
      </div>
      
      {showFileUploadModal && (
        <FileUploadModal
          onClose={() => setShowFileUploadModal(false)}
          onSuccess={handleFileUploadSuccess}
          initialMode={fileUploadMode}
          sessionId={sessionId}
        />
      )}
    </div>
  );
});

export default ChatInput;